import { useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverOptions {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
}

export function useIntersectionObserver(options: UseIntersectionObserverOptions = {}) {
  // threshold 0: fire as soon as any pixel enters the viewport. A non-zero
  // threshold can never be reached by elements taller than ~1/threshold
  // viewports (e.g. long blog bodies on mobile), leaving them stuck invisible.
  const { threshold = 0, root = null, rootMargin = '0px' } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const thresholdStr = JSON.stringify(threshold);

  useEffect(() => {
    const element = ref.current;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        // Once visible, stop observing
        if (element) {
          observer.unobserve(element);
        }
      }
    }, { threshold: JSON.parse(thresholdStr), root, rootMargin });

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [thresholdStr, root, rootMargin]);

  return { ref, isVisible };
}
